/**
 * BookingCard Component
 *
 * Card displaying booking summary with actions.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { HostBooking, BookingStatus } from '../../hooks';

interface BookingCardProps {
  booking: HostBooking;
  onAccept?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
  onMessage?: () => void;
}

const STATUS_CONFIG: Record<BookingStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: 'text-amber-700', bg: 'bg-amber-100' },
  confirmed: { label: 'Confirmed', color: 'text-green-700', bg: 'bg-green-100' },
  active: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-100' },
  completed: { label: 'Completed', color: 'text-gray-700', bg: 'bg-gray-100' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
  declined: { label: 'Declined', color: 'text-gray-500', bg: 'bg-gray-100' },
};

export const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onAccept,
  onDecline,
  onCancel,
  onMessage,
}) => {
  const status = STATUS_CONFIG[booking.status];
  const nights = Math.ceil(
    (booking.checkOut.getTime() - booking.checkIn.getTime()) / (1000 * 60 * 60 * 24)
  );

  const formatDateRange = () => {
    const checkInStr = booking.checkIn.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const checkOutStr = booking.checkOut.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${checkInStr} - ${checkOutStr}`;
  };

  const isPending = booking.status === 'pending';
  const isConfirmed = booking.status === 'confirmed';
  const isActive = booking.status === 'active';
  const canCancel = isConfirmed && booking.checkIn > new Date();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Listing Image */}
        <div className="sm:w-48 h-32 sm:h-auto flex-shrink-0">
          <img
            src={booking.listingImage}
            alt={booking.listingTitle}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <Link
                to={`/host/bookings/${booking.id}`}
                className="font-semibold text-gray-900 hover:text-primary-600 line-clamp-1"
              >
                {booking.listingTitle}
              </Link>
              <p className="text-sm text-gray-500">{formatDateRange()}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
              {status.label}
            </span>
          </div>

          {/* Guest Info */}
          <div className="flex items-center gap-3 mb-3">
            <img
              src={booking.guestAvatar}
              alt={booking.guestName}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="font-medium text-gray-900 text-sm">{booking.guestName}</p>
              <p className="text-xs text-gray-500">
                {booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'} · {nights}{' '}
                {nights === 1 ? 'night' : 'nights'}
              </p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-gray-500">Your payout: </span>
              <span className="font-semibold text-gray-900">${booking.hostPayout}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {isPending && onAccept && onDecline && (
                <>
                  <button
                    onClick={onDecline}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Decline
                  </button>
                  <button
                    onClick={onAccept}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Accept
                  </button>
                </>
              )}

              {(isConfirmed || isActive) && onMessage && (
                <button
                  onClick={onMessage}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                >
                  <MessageIcon className="w-4 h-4" />
                  Message
                </button>
              )}

              {canCancel && onCancel && (
                <button
                  onClick={onCancel}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700"
                >
                  Cancel
                </button>
              )}

              <Link
                to={`/host/bookings/${booking.id}`}
                className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Details →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icons
const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

export default BookingCard;
